import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import NotificationComposer from "@/components/NotificationComposer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { Bell, CheckCheck, Send } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_STYLES = {
  Urgent: "bg-red-50 text-red-600 border-red-200",
  Training: "bg-teal/10 text-teal border-teal/30",
  Documentation: "bg-amber-50 text-amber-700 border-amber-200",
  Policy: "bg-slate-100 text-slate-600 border-slate-200",
  General: "bg-slate-100 text-slate-600 border-slate-200",
  Leave: "bg-teal/10 text-teal border-teal/30",
  Attendance: "bg-teal/10 text-teal border-teal/30",
};

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function MyNotifications() {
  const qc = useQueryClient();
  const [composing, setComposing] = useState(false);

  const { data: items, isLoading } = useQuery({
    queryKey: ["myNotifications"],
    queryFn: async () => (await api.get("/my/notifications")).data,
  });
  // Same query key the sidebar uses, so this is served from cache rather than refetched.
  const { data: team } = useQuery({
    queryKey: ["myTeam"],
    queryFn: async () => (await api.get("/my/team")).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["myNotifications"] });
    qc.invalidateQueries({ queryKey: ["unreadNotifications"] });
  };

  const markRead = useMutation({
    mutationFn: async (nid) => (await api.post(`/my/notifications/${nid}/read`)).data,
    onSuccess: refresh,
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });
  const markAll = useMutation({
    mutationFn: async () => (await api.post("/my/notifications/read-all")).data,
    onSuccess: (d) => { toast.success(d.marked ? `Marked ${d.marked} as read` : "Nothing unread"); refresh(); },
    onError: (e) => toast.error(apiError(e.response?.data?.detail)),
  });

  if (isLoading) return <Loading />;

  const list = items || [];
  const unread = list.filter((n) => !n.read_at).length;

  return (
    <div>
      <PageHeader
        overline="Communications" title="Notifications" icon={Bell}
        subtitle={unread ? `${unread} unread` : "Messages from HR, your reporting officer and the administration."}
        actions={
          <div className="flex gap-2">
            {team?.is_reporting_officer && (
              <Button variant="outline" className="btn-lift" onClick={() => setComposing(true)}
                data-testid="notify-team-btn">
                <Send className="h-4 w-4 mr-1" /> Notify My Team
              </Button>
            )}
            {unread > 0 && (
              <Button className="bg-navy hover:bg-navy-700 btn-lift" onClick={() => markAll.mutate()}
                disabled={markAll.isPending} data-testid="mark-all-read">
                <CheckCheck className="h-4 w-4 mr-1" /> Mark all read
              </Button>
            )}
          </div>
        }
      />

      {list.length === 0 ? (
        <EmptyState icon={Bell} title="No notifications yet"
          hint="Announcements and reminders from HR or your reporting officer will appear here." />
      ) : (
        <div className="space-y-2">
          {list.map((n) => (
            <Card
              key={n.id}
              onClick={() => !n.read_at && markRead.mutate(n.id)}
              className={cn(
                "border-slate-200 shadow-none p-4 transition-colors",
                !n.read_at && "bg-teal/5 border-teal/30 cursor-pointer hover:bg-teal/10"
              )}
              data-testid={`notification-${n.id}`}
            >
              <div className="flex items-start gap-3">
                {!n.read_at && <span className="mt-1.5 h-2 w-2 rounded-full bg-teal shrink-0" aria-label="Unread" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={cn("text-navy", !n.read_at ? "font-semibold" : "font-medium")}>{n.title}</p>
                    <span className={cn("text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border",
                      CATEGORY_STYLES[n.category] || CATEGORY_STYLES.General)}>
                      {n.category}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm mt-1 whitespace-pre-wrap break-words">{n.message}</p>
                  <p className="text-slate-400 text-xs mt-2">
                    {n.sender_name || "IntenCiv HR"} · {when(n.created_at)}
                    {!n.read_at && <span className="text-teal"> · tap to mark read</span>}
                  </p>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <NotificationComposer mode="team" open={composing} onOpenChange={setComposing} />
    </div>
  );
}
